import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, CheckCircle, Loader2, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { trackPurchase, trackTrialUpgrade } from '@/lib/metaPixel';
import QuickNav from '@/components/QuickNav';

import { API } from '@/lib/api';

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Tempo esgotado. Verifique seu email para confirmação.');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true
      });

      setPaymentData(response.data);

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        toast.success('Pagamento confirmado!');
        
        // Track purchase with Meta Pixel
        const planPrice = response.data.package_id === 'premium_monthly' ? 29.90 : 9.90;
        trackPurchase(planPrice, 'BRL', { 
          content_name: response.data.package_id,
          content_type: 'subscription'
        });
        trackTrialUpgrade();
        
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Sessão de pagamento expirada.');
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      <motion.div
        className="max-w-md w-full text-center"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 text-[#00FF94] animate-spin mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-white mb-4">
              Verificando pagamento...
            </h1>
            <p className="text-gray-400">
              Aguarde enquanto confirmamos seu pagamento
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#00FF94]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-[#00FF94]" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Pagamento Confirmado!
            </h1>
            <p className="text-gray-400 mb-8">
              {paymentData?.package_id === 'premium_monthly' 
                ? 'Sua assinatura Premium foi ativada com sucesso!' 
                : 'Seu pagamento foi processado com sucesso!'}
            </p>
            
            <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-6 mb-8">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Valor pago</span>
                <span className="text-2xl font-bold text-[#00FF94] font-mono">
                  R$ {paymentData?.amount?.toFixed(2) || '0.00'}
                </span>
              </div>
            </div>

            <div className="space-y-3">
              {paymentData?.package_id === 'premium_monthly' ? (
                <Button
                  className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold py-6"
                  onClick={() => navigate('/dashboard')}
                >
                  Ir para o Dashboard
                </Button>
              ) : (
                <Button
                  className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold py-6"
                  onClick={() => navigate('/analyze')}
                >
                  Fazer Análise
                </Button>
              )}
              <Button
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5 rounded-full"
                onClick={() => navigate('/')}
              >
                Voltar ao Início
              </Button>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-12 h-12 text-red-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-4">
              Erro no Pagamento
            </h1>
            <p className="text-gray-400 mb-8">
              Não foi possível confirmar seu pagamento. Por favor, tente novamente ou entre em contato conosco.
            </p>
            
            <div className="space-y-3">
              <Button
                className="w-full bg-[#00FF94] text-black hover:bg-[#00CC76] rounded-full font-bold py-6"
                onClick={() => navigate('/pricing')}
              >
                Tentar Novamente
              </Button>
              <Button
                variant="outline"
                className="w-full border-white/10 text-white hover:bg-white/5 rounded-full"
                onClick={() => navigate('/')}
              >
                Voltar ao Início
              </Button>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-12 flex items-center justify-center gap-2 text-gray-500">
          <Shield className="w-4 h-4" />
          <span className="text-sm">Evite Dívidas</span>
        </div>
      </motion.div>
      <QuickNav />
    </div>
  );
};

export default PaymentSuccess;
